import React, { createContext, useContext } from 'react';

import { useNetwork } from './NetworkContext';
import { getAllSupportedVersions } from '@/data/SupportedVersions';
import { useToast } from '@/hooks';
import { APP_VERSION } from '@/utils/version';
import {
  UseQueryResult,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

interface VersionContextProps {
  forceUpdate: boolean | null;
  maintenanceModeEnabled: boolean | null;
  supportedVersions: string[];
}

const VersionContext = createContext<VersionContextProps | undefined>(
  undefined
);

export const VersionProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { connected } = useNetwork();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const {
    data: versionData
  }: UseQueryResult<VersionContextProps | null, Error> = useQuery({
    queryKey: ['supportedVersions'],
    queryFn: async () => {
      if (!connected) return null;

      try {
        const supportedVersions = await getAllSupportedVersions();
        const versions = supportedVersions.map(
          (supportedVersion) => supportedVersion.version
        );

        const currentVersion = supportedVersions.find(
          (supportedVersion) => supportedVersion.version === APP_VERSION
        );

        return {
          supportedVersions: versions,
          forceUpdate: !currentVersion,
          maintenanceModeEnabled:
            currentVersion?.maintenance_mode_enabled ?? null
        };
      } catch {
        displayToast({
          message: 'Please check your internet connection.',
          duration: 5000,
          position: 'bottom'
        });
        return null;
      }
    },
    enabled: connected
  });

  const { forceUpdate, maintenanceModeEnabled, supportedVersions } =
    versionData ?? {};

  return (
    <VersionContext.Provider
      value={{
        forceUpdate: forceUpdate ?? null,
        maintenanceModeEnabled: maintenanceModeEnabled ?? null,
        supportedVersions: supportedVersions ?? []
      }}
    >
      {children}
    </VersionContext.Provider>
  );
};

export const useVersion = (): VersionContextProps => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  return context;
};
